import querystring from 'query-string';
import {useEffect, useRef} from 'react';
import {Linking, Platform} from 'react-native';
import {AppDispatch} from '../../redux';
import {Routes} from '../routing';
import {deepLinkScheme as deepLinkPrefix} from '../constants';
import {addNotification} from '../../redux/actions/snackbar';
import {SnackbarNotificationType} from '../components/snackbar/model';
import {navigateToProfileEmailConfirmation} from '../routing/profileEmailConfirmationNav';

export const isChangeEmailOp = (op: string) => op.toLowerCase() === 'changeemail';
export const isChangeEmailConfirmation = (op: string, code?: string) => isChangeEmailOp(op) && !!code;

const queryParam = (value: string | (string | null)[] | null) => {
  if (Array.isArray(value)) {
    return String(value[0] ?? '');
  }

  return String(value ?? '');
};

export const parseConfirmationQuery = (urlOrPath: string) => {
  const query = urlOrPath.includes('?') ? urlOrPath.slice(urlOrPath.indexOf('?') + 1) : '';
  const {op, code} = querystring.parse(query);

  return {op: queryParam(op).toLowerCase(), code: queryParam(code)};
};

const isConfirmationUrl = (url: string) => {
  if (!url.startsWith(deepLinkPrefix) || !url.includes('op=')) {
    return false;
  }

  const transformedUrl = url.toLowerCase();
  return (
    !transformedUrl.includes('email-confirmation') &&
    (transformedUrl.includes('/?link=confirmation') || /confirmation(?:\?|$|&)/.test(transformedUrl))
  );
};

const openLegacyConfirmationUrl = (url: string, opName: string) => {
  const keyRoute = '/?link=confirmation';
  const targetRoute = opName.startsWith('restore') ? Routes.loginConfirmation : Routes.profileConfirmation;
  const query = url.includes('?') ? url.slice(url.indexOf('?')) : '';
  const target = url.toLowerCase().includes(keyRoute)
    ? url.replace(keyRoute, `${targetRoute}?`)
    : `${deepLinkPrefix}/${targetRoute.replace(/^\//, '')}${query}`;

  Linking.openURL(target);
};

const handleConfirmationUrl = (url: string, dispatch: AppDispatch) => {
  const {op, code} = parseConfirmationQuery(url);
  const opName = op.toLowerCase();

  if (isChangeEmailOp(op)) {
    if (!code) {
      dispatch(
        addNotification({
          message: 'Отсутствует код подтверждения. Попробуйте снова',
          type: SnackbarNotificationType.Error,
        }),
      );
      return;
    }

    navigateToProfileEmailConfirmation(op, code);
    return;
  }

  if (opName === 'createpassemail' || opName.startsWith('change') || opName.startsWith('restore')) {
    openLegacyConfirmationUrl(url, opName);
  }
};

type Options = {
  dispatch: AppDispatch;
  isAppDataRestored: boolean;
  isAuthenticated: boolean;
  isAuthenticatedByPassword: boolean;
};

export const useConfirmationDeepLink = ({
  dispatch,
  isAppDataRestored,
  isAuthenticated,
  isAuthenticatedByPassword,
}: Options) => {
  const pendingUrlRef = useRef<string | null>(null);
  const canHandle = isAppDataRestored && isAuthenticated && isAuthenticatedByPassword;

  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    const onUrl = (url: string | null) => {
      if (!url || !isConfirmationUrl(url)) {
        return;
      }

      if (!canHandle) {
        pendingUrlRef.current = url;
        return;
      }

      pendingUrlRef.current = null;
      handleConfirmationUrl(url, dispatch);
    };

    Linking.getInitialURL().then(onUrl);
    const subscription = Linking.addEventListener('url', ({url}) => onUrl(url));

    return () => subscription.remove();
  }, [canHandle, dispatch]);

  useEffect(() => {
    if (!canHandle || !pendingUrlRef.current) {
      return;
    }

    handleConfirmationUrl(pendingUrlRef.current, dispatch);
    pendingUrlRef.current = null;
  }, [canHandle, dispatch]);
};
